import React from 'react';
import { cn } from '~/lib/utils';
import { ActivityGlyph } from './ActivityGlyph';
import styles from './BubbleFooter.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface BubbleFooterProps {
  /**
   * Drives both halves of the toggle at once: `false` reads "Show more" over a down chevron,
   * `true` reads "Show less" over an up one. It is the bubble's clamp state, passed down rather
   * than held here, because the bubble and this row have to agree or the label lies.
   */
  expanded: boolean;
  /**
   * Rendered as "{n} characters". The count is of the message, not of what is visible — *Very
   * long* (724) and *Long · clamped* (438) clamp to the identical 168px box, and the count is the
   * only thing that tells them apart.
   */
  characterCount: number;
  onToggle: () => void;
  theme?: ChatTheme;
  className?: string;
}

/**
 * The line under a clamped user message: a disclosure on the left, a character count on the right.
 *
 * Drawn on *Long · clamped*, *Long · expanded* and *Very long*. It is a **sibling** of
 * `UserBubble`, never a part of it — Paper hangs it 4px below the bubble, outside the clip, which
 * is what lets the bubble be `overflow: clip` and this row still be readable and clickable.
 *
 * The bubble deliberately carries no `aria-expanded`: the clamped text stays in the DOM and stays
 * readable to a screen reader, so the clamp is a convenience for the eye rather than a redaction.
 * The state therefore lives here, on the real button that changes it.
 */
export const BubbleFooter = React.forwardRef<HTMLDivElement, BubbleFooterProps>(
  function BubbleFooter({ expanded, characterCount, onToggle, theme: themeProp, className }, ref) {
  const theme = useV15Theme(themeProp);
    return (
      <div ref={ref} className={cn(chatThemeClass(theme), styles.footer, className)}>
        <button type="button" className={styles.toggle} aria-expanded={expanded} onClick={onToggle}>
          {expanded ? 'Show less' : 'Show more'}
          {/* Two glyphs rather than one rotated 180deg: `arrow-up-s` is separately drawn, with its
              apex 0.5px off a rotation of `arrow-down-s`, and matching the file beats buying an
              animation with a shape that is slightly wrong. `ActivityGlyph` names both, so the
              choice is made once there instead of once per disclosure in the folder.

              14 for BOTH directions here, where `ActivityRow` steps 15 up / 14 down — this row is
              a 13px text run and the mark is sized to the words, not to a row tier.

              `inherit`, because `.chevron` holds the chevron one rung below the label in both
              states (hint under muted at rest, muted under subtle on hover) and that two-tone
              relationship is a single rule about this row. */}
          <ActivityGlyph
            className={styles.chevron}
            chrome={expanded ? 'chevron-up' : 'chevron-down'}
            size={14}
            tone="inherit"
            theme={theme}
          />
        </button>
        {/* Singular below two, because "1 characters" is the kind of thing that survives review and
            then embarrasses everyone. Paper only ever draws 438 and 724. */}
        <span className={styles.count}>
          {characterCount} {characterCount === 1 ? 'character' : 'characters'}
        </span>
      </div>
    );
  }
);
